import isWebos from '@accedo/xdk-device-lg-webos/esm/detection'
import isVizio from '@accedo/xdk-device-vizio-smartcast-contrib/esm/detection'
import { Lightning, Router } from '@lightningjs/sdk'

import Button from '../../../components/button/button'
import PointerBackButton from '../../../components/NavigationPointers/PointerBackButton'
import { getColor, grey300, grey50, grey920, transparent } from '../../../constants/colors'
import { UniNeueBold, UniNeueHeavy, UniNeueRegular } from '../../../constants/fonts'
import { LEFT_SAFE_ZONE_MARGIN } from '../../../constants/general'
import { Images } from '../../../constants/paths'
import { LG_SIGN_UP, VIZIO_SIGN_UP, LOGIN, SIGN_UP } from '../../../constants/routes'
import { AppConfig } from '../../../services/appConfig'
import { Flo } from '../../../types/flo-auth-api'
import {
  getUserData,
  isUserSignedIn,
  isUserSubscriptionActive,
  updateUserSubscriptionData,
} from '../../../utils/authentication'
import { formatDate } from '../../../utils/datetime'
import { getText } from '../../../utils/dictionary'
import { getAsset } from '../../../utils/image'
import { generateQR } from '../../../utils/misc'
import {
  captureClick,
  captureScreenData,
  NON_BFF_SEGMENT_SCREENS,
  SEGMENT_TRACK_EVENTS,
} from '../../../utils/segmentAnalytics'
import PageHandler from '../../handlers/pageHandler'

interface HistoryState {
  /** Stores the focused ref when navigating away. */
  lastFocus?: string
}

const VIZIO_SUBSCRIPTION_TYPE = 'vizio'

export default class Subscription extends PageHandler {
  private _isUserSignedIn = isUserSignedIn()
  private _curButton: Button
  private _contextData: SubscriptionContextData
  private _subscription: Flo.Auth.UserSubscription
  private _sectionText = ''

  static _template() {
    return {
      w: 1920,
      h: 1080,
      rect: true,
      color: transparent,
      collision: true,
      flex: {
        direction: 'row',
        paddingLeft: LEFT_SAFE_ZONE_MARGIN,
      },
      LeftContainer: {
        w: 872,
        h: 1080,
        rect: true,
        color: transparent,
        Title: {
          y: 48,
          text: {
            text: getText('subscription'),
            fontSize: 64,
            fontFace: UniNeueHeavy,
            textColor: getColor(grey50),
            lineHeight: 72,
          },
        },
        Icon: {
          x: 86,
          y: 339,
          texture: Lightning.Tools.getSvgTexture(getAsset(Images.SETTINGS_SUBSCRIPTION), 472, 418),
        },
        PointerBackButton: {
          type: PointerBackButton,
          x: 1648,
          y: 48,
        },
      },
      RightContainer: {
        h: 1080,
        rect: true,
        color: transparent,
        InfoCard: {
          y: 200,
          w: 808,
          h: 800,
          rect: true,
          color: getColor(grey920),
          shader: {
            type: Lightning.shaders.RoundedRectangle,
            radius: 40,
          },
          Unauthenticated: {
            w: (w) => w,
            h: (h) => h,
            flex: {
              direction: 'column',
              alignItems: 'center',
              paddingTop: 40,
            },
            Title: {
              text: {
                text: getText('loginHeader'),
                fontSize: 28,
                fontFace: UniNeueBold,
                textColor: getColor(grey50),
                lineHeight: 49,
              },
            },
            Login: {
              w: 668,
              type: Button,
              label: getText('loginButton'),
            },
            SignUp: {
              flexItem: {
                marginTop: 32,
              },
              w: 668,
              type: Button,
              label: getText('signUpButton'),
            },
          },
          Authenticated: {
            w: (w) => w,
            h: (h) => h,
            flex: {
              direction: 'column',
              paddingTop: 40,
            },
            UserData: {
              x: 40,
              flex: {
                direction: 'column',
              },
              SubscriptionTypeHeader: {
                text: {
                  text: getText('subscriptionTypeHeader'),
                  fontSize: 28,
                  fontFace: UniNeueRegular,
                  textColor: getColor(grey300),
                },
              },
              SubscriptionTypeValue: {
                flexItem: { marginBottom: 16 },
                text: {
                  text: '',
                  fontSize: 32,
                  fontFace: UniNeueBold,
                  textColor: getColor(grey50),
                  lineHeight: 36,
                },
              },
              SubscriptionStatusHeader: {
                text: {
                  text: getText('subscriptionStatusHeader'),
                  fontSize: 28,
                  fontFace: UniNeueRegular,
                  textColor: getColor(grey300),
                },
              },
              SubscriptionStatusValue: {
                flexItem: { marginBottom: 16 },
                text: {
                  text: '',
                  fontSize: 32,
                  fontFace: UniNeueBold,
                  textColor: getColor(grey50),
                  lineHeight: 36,
                },
              },
              NextPaymentHeader: {
                text: {
                  text: getText('nextPaymentHeader'),
                  fontSize: 28,
                  fontFace: UniNeueRegular,
                  textColor: getColor(grey300),
                },
              },
              NextPaymentValue: {
                flexItem: { marginBottom: 16 },
                text: {
                  text: '',
                  fontSize: 32,
                  fontFace: UniNeueBold,
                  textColor: getColor(grey50),
                  lineHeight: 36,
                },
              },
              SignUpAuthenticated: {
                visible: false,
                flexItem: {
                  marginTop: 32,
                },
                w: 668,
                type: Button,
                label: getText('signUpButton'),
              },
            },
            ManageSubscription: {
              h: 244,
              w: 658,
              x: 40,
              y: 132,
              visible: false,
              flex: { direction: 'row', alignItems: 'center' },
              QRCode: {
                w: 244,
                h: 244,
                rect: true,
                color: getColor(grey50),
                shader: null,
                src: '',
                flexItem: { marginRight: 32 },
              },
              Content: {
                flex: { direction: 'column' },
                Header: {
                  text: {
                    text: getText('manageSubscriptionHeader'),
                    fontSize: 32,
                    fontFace: UniNeueBold,
                    textColor: getColor(grey50),
                    lineHeight: 36,
                  },
                },
                Description: {
                  text: {
                    text: getText('manageSubscriptionDesc'),
                    fontSize: 24,
                    fontFace: UniNeueRegular,
                    textColor: getColor(grey300),
                    lineHeight: 34,
                    wordWrapWidth: 390,
                    maxLines: 4,
                    maxLinesSuffix: '...',
                  },
                },
              },
            },
          },
        },
      },
    }
  }

  get unauthenticated() {
    return this.tag('Unauthenticated')
  }

  get authenticated() {
    return this.tag('Authenticated')
  }

  get subscriptionTypeValue() {
    return this.tag('SubscriptionTypeValue')
  }

  get subscriptionStatusValue() {
    return this.tag('SubscriptionStatusValue')
  }

  get nextPaymentValue() {
    return this.tag('NextPaymentValue')
  }

  get loginButton() {
    return this.tag('Login') as Button
  }

  get signUpButton() {
    return (this._isUserSignedIn ? this.tag('SignUpAuthenticated') : this.tag('SignUp')) as Button
  }

  get qrCode() {
    return this.tag('QRCode')
  }

  get qrCodeHeader() {
    return this.tag('Header')
  }

  get qrCodeDescription() {
    return this.tag('Description')
  }

  set params(v: SubscriptionContextData) {
    this._contextData = v
  }

  get manageSubscriptionContainer() {
    return this.tag('ManageSubscription')
  }

  get pageText(): string {
    return getText('subscription')
  }

  set sectionText(v: string) {
    this._sectionText = v
  }

  get sectionText(): string {
    return this._sectionText
  }

  get pointerBackButton() {
    return this.tag('PointerBackButton') as PointerBackButton
  }

  async _init() {
    this._curButton ??= null

    if (this._isUserSignedIn) {
      this.unauthenticated.visible = false

      await updateUserSubscriptionData()
      this._subscription = getUserData()?.user?.user_subscriptions?.[0]

      if (!isUserSubscriptionActive()) {
        this.signUpButton.visible = true
        this._curButton = this.signUpButton
      } else {
        this._curButton = null
      }

      this.renderUserData()
    } else {
      this._curButton = this.loginButton
      this.sectionText = getText('loginHeader') + ' . . '
      this.authenticated.visible = false
    }

    void captureScreenData(NON_BFF_SEGMENT_SCREENS.Settings, NON_BFF_SEGMENT_SCREENS.Subscription)

    const { isCursorVisible } = AppConfig.shared.appSettings
    this.pointerBackButton.visible = isCursorVisible
  }

  _getFocused() {
    return this._curButton
  }

  _handleEnter() {
    if (this._curButton === this.loginButton) {
      void captureClick(SEGMENT_TRACK_EVENTS.ButtonClicked, 'Log In', 'Settings', 'Subscription')

      Router.navigate(LOGIN)
    } else if (this.signUpButton.visible && this._curButton === this.signUpButton) {
      void captureClick(SEGMENT_TRACK_EVENTS.ButtonClicked, 'Sign Up', 'Settings', 'Subscription')

      if (isWebos) {
        Router.navigate(LG_SIGN_UP)
      }
      else if (isVizio && !window?.VIZIO?.Account?.isSVODEnabled()) {
        Router.navigate(VIZIO_SIGN_UP)
      }
      else {
        Router.navigate(SIGN_UP, { contextData: this._contextData, fromSettings: true })
      }
    }
  }

  _handleLeft() {
    Router.focusWidget('Menu')
  }

  _handleUp() {
    if (!this._isUserSignedIn) {
      this._curButton = this.loginButton
    }
  }

  _handleDown() {
    if (this.signUpButton.visible) {
      this._curButton = this.signUpButton
    }
  }

  historyState(params: HistoryState) {
    if (params?.lastFocus) {
      this._curButton = this.tag(params.lastFocus) as Button
    } else {
      return {
        lastFocus: this._getFocused()?.ref,
      }
    }
  }

  async renderUserData() {
    let endDate = this._subscription?.end_date

    if (this._subscription?.is_active) {
      this.manageSubscriptionContainer.visible = true

      if (this._subscription?.offer_type === VIZIO_SUBSCRIPTION_TYPE) {
        this.qrCode.visible = false
        this.qrCodeDescription.patch({
          text: { text: getText('manageSubscriptionDescVizio'), wordWrapWidth: 600 },
        })
      } else {
        this.qrCode.visible = true
        await this.setQR(`${getText('manageSubscriptionUrl')}${this._subscription?.id}`)
        this.qrCodeDescription.patch({
          text: { text: getText('manageSubscriptionDesc'), wordWrapWidth: 390 },
        })
      }
    }

    if (this.signUpButton.visible && !this._subscription?.is_active) {
      this.signUpButton.label = getText('paywallManagerSubscription')
    }

    this.subscriptionTypeValue.patch({
      text: {
        text: getText(
          this._subscription?.is_active ? 'subscriptionTypePro' : 'subscriptionTypeFree',
        ),
      },
    })
    this.sectionText =
      getText('subscriptionTypeHeader') +
      ' . . ' +
      getText(this._subscription?.is_active ? 'subscriptionTypePro' : 'subscriptionTypeFree') +
      ' . . '

    this.subscriptionStatusValue.patch({
      text: { text: this._subscription?.status ?? '-' },
    })
    this.sectionText +=
      getText('subscriptionStatusHeader') + ' . . ' + this._subscription?.status + ' . . '

    this.nextPaymentValue.patch({
      text: { text: formatDate(endDate) ?? '-' },
    })
    this.sectionText += getText('nextPaymentHeader') + ' . . ' + formatDate(endDate) + ' . . '
    this.speak()
    this._refocus()
  }

  async setQR(textToQr: string) {
    this.qrCode.patch({ src: await generateQR(textToQr) })
  }

  $updateFocusByHover(button) {
    this._curButton = button
    this._refocus()
  }

  _handleClick() {
    this._handleEnter()
  }

  _onCursorEnter() {
    const { isCursorVisible } = AppConfig.shared.appSettings
    this.pointerBackButton.visible = isCursorVisible
  }

  _onCursorLeave() {
    this._curButton?._focus()
    this.pointerBackButton.visible = false
  }
}
